import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { usePasscode } from '../../components/common/PasscodeModal';
import { depositCheque, returnCheque, CHEQUES_IN_HAND_ID } from '../../api/cheques';

const fmt = (n) => Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '-';
const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : 0;
// A cheque is normally only valid for 6 months from its date — one sitting in hand longer
// than that is likely stale and needs chasing / replacing before it can be deposited.
const STALE_DAYS = 180;

const STATUS_STYLE = {
  IN_HAND:  { bg: '#fef9c3', color: '#854d0e', label: 'In Hand' },
  BANKED:   { bg: '#dcfce7', color: '#15803d', label: 'Banked' },
  RETURNED: { bg: '#fee2e2', color: '#b91c1c', label: 'Returned' },
  VOID:     { bg: '#f3f4f6', color: '#6b7280', label: 'Void' },
};

export default function ChequeDashboard() {
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  const [cheques, setCheques] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [tab, setTab] = useState('IN_HAND');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [depositAccount, setDepositAccount] = useState('');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().slice(0, 10));
  const [depositRef, setDepositRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnReason, setReturnReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase.from('receipt_vouchers')
      .select('id, voucher_number, voucher_date, received_from, client_id, amount, base_amount, currency_id, exchange_rate, bank_reference, description, voucher_status, branch_id, journal_entry_id, suspense_cleared_journal_id, reference_type, reference_id, is_suspense, banked_at, banked_into_account_id, returned_at, returned_by, return_reason, void_reason, currencies(code), clients(full_name, client_code)')
      .eq('payment_mode', 'Cheque')
      .order('voucher_date', { ascending: true });
    if (err) setError(err.message);
    setCheques(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.from('chart_of_accounts')
      .select('id, account_code, account_name, account_types(category)')
      .eq('is_active', true).order('account_code')
      .then(({ data }) => setAccounts(data || []));
    supabase.from('branches').select('id, name').then(({ data }) => setBranches(data || []));
  }, [load]);

  const bankAccounts = useMemo(() => accounts.filter(a => a.account_types?.category === 'ASSET' && a.id !== CHEQUES_IN_HAND_ID), [accounts]);
  const accountName = (id) => { const a = accounts.find(x => x.id === id); return a ? `${a.account_code} · ${a.account_name}` : '—'; };
  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';
  const base = (c) => Number(c.base_amount || c.amount || 0);

  const stats = useMemo(() => {
    const by = (st) => cheques.filter(c => c.voucher_status === st);
    const inHand = by('IN_HAND');
    const sum = (list) => list.reduce((s, c) => s + base(c), 0);
    const bucket = (lo, hi) => inHand.filter(c => { const d = daysSince(c.voucher_date); return d >= lo && d <= hi; });
    return {
      inHand: { n: inHand.length, total: sum(inHand) },
      banked: { n: by('BANKED').length, total: sum(by('BANKED')) },
      returned: { n: by('RETURNED').length, total: sum(by('RETURNED')) },
      oldest: inHand.length ? Math.max(...inHand.map(c => daysSince(c.voucher_date))) : 0,
      aging: [
        { label: '0–30 days', list: bucket(0, 30), color: '#16a34a', bg: '#f0fdf4' },
        { label: '31–90 days', list: bucket(31, 90), color: '#d97706', bg: '#fffbeb' },
        { label: '91–180 days', list: bucket(91, STALE_DAYS), color: '#ea580c', bg: '#fff7ed' },
        { label: `Stale (>${STALE_DAYS}d)`, list: bucket(STALE_DAYS + 1, 99999), color: '#dc2626', bg: '#fef2f2' },
      ].map(b => ({ ...b, n: b.list.length, total: sum(b.list) })),
    };
  }, [cheques]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cheques
      .filter(c => tab === 'ALL' ? true : c.voucher_status === tab)
      .filter(c => !term || [c.voucher_number, c.received_from, c.bank_reference, c.clients?.full_name, c.clients?.client_code]
        .some(v => (v || '').toLowerCase().includes(term)))
      .sort((a, b) => tab === 'IN_HAND' ? a.voucher_date.localeCompare(b.voucher_date) : b.voucher_date.localeCompare(a.voucher_date));
  }, [cheques, tab, search]);

  const inHandVisible = visible.filter(c => c.voucher_status === 'IN_HAND');
  const selectedCheques = cheques.filter(c => selected.has(c.id) && c.voucher_status === 'IN_HAND');
  const totalSelected = selectedCheques.reduce((s, c) => s + Number(c.amount || 0), 0);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === inHandVisible.length ? new Set() : new Set(inHandVisible.map(c => c.id)));

  const handleDeposit = async () => {
    if (!depositAccount) return setError('Select a bank account to deposit into.');
    if (!selectedCheques.length) return setError('Select at least one cheque to deposit.');
    setBusy(true); setError(''); setSuccess('');
    try {
      for (const c of selectedCheques) await depositCheque(c, { accountId: depositAccount, date: depositDate, ref: depositRef, username });
      setSuccess(`${selectedCheques.length} cheque(s) deposited — LKR ${fmt(totalSelected)}`);
      setSelected(new Set()); setDepositRef('');
      await load();
    } catch (e) { setError(e.message || 'Deposit failed'); }
    setBusy(false);
  };

  const handleReturn = async () => {
    if (!returnTarget || !returnReason.trim()) return;
    try { await promptPasscode(); } catch { return; }
    setBusy(true); setError(''); setSuccess('');
    try {
      const r = await returnCheque(returnTarget, { reason: returnReason.trim(), username });
      const who = returnTarget.clients?.full_name || returnTarget.received_from || 'the payer';
      setSuccess(
        `Cheque ${returnTarget.bank_reference || returnTarget.voucher_number} returned. ` +
        (r.hadClient ? `LKR ${fmt(base(returnTarget))} is due from ${who} again.` : 'It was an unidentified deposit — removed from suspense, no client balance affected.') +
        (r.reopened.length ? ` Invoice(s) reopened: ${r.reopened.join(', ')}.` : '') +
        (r.movedToAr > 0 ? ` LKR ${fmt(r.movedToAr)} of it had already been applied from the client's deposit — restored onto their AR.` : '')
      );
      setReturnTarget(null); setReturnReason('');
      await load();
    } catch (e) { setError(e.message || 'Return failed'); }
    setBusy(false);
  };

  const card = { background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' };
  const tabs = [['IN_HAND', 'In Hand'], ['BANKED', 'Banked'], ['RETURNED', 'Returned'], ['ALL', 'All']];

  return (
    <div style={{ padding: 24 }}>
      {PasscodeModal}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Cheque Dashboard</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Cheques received — in hand, deposited, and returned</div>
        </div>
        <Link to="/vouchers/receipt/new" className="btn btn-primary">+ New Receipt</Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
        <div style={{ ...card, borderLeft: '3px solid #ca8a04' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>In Hand</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#854d0e' }}>LKR {fmt(stats.inHand.total)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stats.inHand.n} cheque{stats.inHand.n === 1 ? '' : 's'}{stats.inHand.n ? ` · oldest ${stats.oldest}d` : ''}</div>
        </div>
        <div style={{ ...card, borderLeft: '3px solid #16a34a' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Banked</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#15803d' }}>LKR {fmt(stats.banked.total)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stats.banked.n} cheque{stats.banked.n === 1 ? '' : 's'}</div>
        </div>
        <div style={{ ...card, borderLeft: '3px solid #dc2626' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Returned</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#b91c1c' }}>LKR {fmt(stats.returned.total)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stats.returned.n} cheque{stats.returned.n === 1 ? '' : 's'}</div>
        </div>
        <div style={{ ...card, borderLeft: `3px solid ${stats.aging[3].n ? '#dc2626' : '#94a3b8'}` }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Stale in hand (&gt;{STALE_DAYS} days)</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: stats.aging[3].n ? '#b91c1c' : 'var(--text-muted)' }}>{stats.aging[3].n}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stats.aging[3].n ? `LKR ${fmt(stats.aging[3].total)} — likely past validity` : 'none'}</div>
        </div>
      </div>

      {stats.inHand.n > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 18 }}>
          {stats.aging.map(b => (
            <div key={b.label} style={{ background: b.bg, border: `1px solid ${b.color}33`, borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: b.color }}>{b.label} in hand</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: b.color }}>LKR {fmt(b.total)} <span style={{ fontWeight: 400, fontSize: 11 }}>({b.n})</span></div>
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 13, padding: '8px 12px', background: 'rgba(239,68,68,0.06)', borderRadius: 6 }}>{error}</div>}
      {success && <div style={{ color: '#15803d', marginBottom: 12, fontSize: 13, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6 }}>{success}</div>}

      {tab === 'IN_HAND' && selectedCheques.length > 0 && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#854d0e', marginBottom: 4 }}>Deposit Into Bank *</div>
            <select className="form-input" value={depositAccount} onChange={e => setDepositAccount(e.target.value)} style={{ width: 240 }}>
              <option value="">-- Select bank account --</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} · {a.account_name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#854d0e', marginBottom: 4 }}>Deposit Date</div>
            <input type="date" className="form-input" value={depositDate} onChange={e => setDepositDate(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#854d0e', marginBottom: 4 }}>Slip / Reference</div>
            <input className="form-input" value={depositRef} onChange={e => setDepositRef(e.target.value)} placeholder="Deposit slip no." style={{ width: 180 }} />
          </div>
          <button className="btn btn-primary" onClick={handleDeposit} disabled={busy}>
            {busy ? 'Depositing…' : `Deposit ${selectedCheques.length} cheque${selectedCheques.length === 1 ? '' : 's'} — LKR ${fmt(totalSelected)}`}
          </button>
        </div>
      )}

      <div style={{ ...card, padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {tabs.map(([k, l]) => (
              <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setTab(k); setSelected(new Set()); }}>
                {l}{k !== 'ALL' && ` (${cheques.filter(c => c.voucher_status === k).length})`}
              </button>
            ))}
          </div>
          <input className="form-input" placeholder="Search voucher, cheque no, client or payer…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 320 }} />
        </div>

        {loading ? <div className="spinner" style={{ margin: 40 }} /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                  {tab === 'IN_HAND' && <th style={{ padding: '10px 12px', width: 32 }}><input type="checkbox" checked={inHandVisible.length > 0 && selected.size === inHandVisible.length} onChange={toggleAll} /></th>}
                  {['Voucher', 'Date', tab === 'IN_HAND' ? 'Held' : null, 'Client / Payer', 'Cheque No', 'Amount', 'Branch', 'Status', 'Details', ''].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(c => {
                  const st = STATUS_STYLE[c.voucher_status] || { bg: '#f3f4f6', color: '#555', label: c.voucher_status };
                  const held = daysSince(c.voucher_date);
                  const stale = c.voucher_status === 'IN_HAND' && held > STALE_DAYS;
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--border)', opacity: c.voucher_status === 'VOID' ? 0.55 : 1 }}>
                      {tab === 'IN_HAND' && <td style={{ padding: '9px 12px' }}><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>}
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12 }}><Link to={`/vouchers/receipt/${c.id}`}>{c.voucher_number}</Link></td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(c.voucher_date)}</td>
                      {tab === 'IN_HAND' && <td style={{ padding: '9px 12px', color: stale ? '#dc2626' : held > 90 ? '#ea580c' : 'var(--text-muted)', fontWeight: stale ? 700 : 400 }}>{held}d{stale && ' ⚠'}</td>}
                      <td style={{ padding: '9px 12px' }}>
                        {c.clients ? <><div style={{ fontWeight: 500 }}>{c.clients.full_name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.clients.client_code}</div></>
                          : <><div>{c.received_from || '—'}</div>{c.is_suspense && <div style={{ fontSize: 11, color: '#92400e' }}>Unidentified deposit</div>}</>}
                      </td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace' }}>{c.bank_reference || '—'}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{c.currencies?.code && c.currencies.code !== 'LKR' ? `${c.currencies.code} ` : ''}{fmt(c.amount)}</td>
                      <td style={{ padding: '9px 12px', color: 'var(--text-muted)' }}>{branchName(c.branch_id)}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: st.bg, color: st.color }}>{st.label}</span>
                      </td>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-muted)', maxWidth: 260 }}>
                        {c.voucher_status === 'BANKED' && <>Deposited {fmtDate(c.banked_at)}{c.banked_into_account_id ? ` → ${accountName(c.banked_into_account_id)}` : ''}</>}
                        {c.voucher_status === 'RETURNED' && <span style={{ color: '#b91c1c' }}>{fmtDate(c.returned_at)} by {c.returned_by} — {c.return_reason}</span>}
                        {c.voucher_status === 'IN_HAND' && (c.description || '')}
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        {['IN_HAND', 'BANKED'].includes(c.voucher_status) && (
                          <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', border: 'none' }}
                            onClick={() => { setReturnTarget(c); setReturnReason(''); setError(''); setSuccess(''); }}>Return</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!visible.length && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No cheques here.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {returnTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 10, padding: 24, width: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Return Cheque</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
              {returnTarget.voucher_number} · cheque {returnTarget.bank_reference || '—'} · LKR {fmt(base(returnTarget))}
              <br />{returnTarget.clients?.full_name || returnTarget.received_from || 'Unidentified payer'}
            </div>
            <div style={{ fontSize: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, padding: '8px 10px', marginBottom: 14 }}>
              {returnTarget.voucher_status === 'BANKED' ? 'Reverses the receipt and the bank deposit (money leaves the bank)' : 'Reverses the receipt'}
              {returnTarget.client_id ? ' — the amount becomes due from the client again, and any invoice it settled is reopened.' : ' — no client is attached, so it is just removed from suspense.'}
              {' '}Reversing entries are posted; nothing is deleted.
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Reason for return *</label>
              <input className="form-input" value={returnReason} onChange={e => setReturnReason(e.target.value)} placeholder="e.g. Insufficient funds, signature mismatch…" autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setReturnTarget(null)} disabled={busy}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={handleReturn} disabled={!returnReason.trim() || busy}>
                {busy ? 'Processing…' : 'Confirm Return'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
